import { useShowTemplateIsBeingSavedToast } from '@/components/FormCreator/hooks/useShowTemplateIsBeingSavedToast';
import { EditorMediaManager } from '@/components/TemplateEditor/components/EditorMediaManager';
import { linkToMergeFields } from '@/components/TemplateEditor/constants'; // TO BE UPDATED
import { useHistoryBackActionContext } from '@/services/hooks/useHistoryBackAction';
import { usePlanHaveRecaptchaFeature } from '@/services/hooks/usePlanHaveRecaptchaFeature';
import { useUpselling } from '@/services/hooks/useUpselling';
import { LockIcon } from '@/services/icons/LockedIcon';
import { isIntercomEnabled } from '@/services/Secuirty';
import { getGeneralInfo } from '@/store/account/actions';
import { getAccount } from '@/store/account/types';
import { getDialogStore, HideDialog, ShowDialog } from '@/store/dialog/actions';
import {
    getFormCreatorStore,
    ModifyForm,
    OpenUpsellingOverlay,
    SetIsTemplateOpen,
    SetTemplatePreview,
    TemplateChanged,
    TemplateSaveStart,
    TemplateShowSaveErrors,
    UpdateContactLists,
} from '@/store/formCreator/actions';
import { ListsGetNamesStart } from '@/store/lists/actions';
import { getMediaManagerStore, ShowMediaManager } from '@/store/mediaManager/actions';
import { getEditorFilesStore, UploadEditorFile, UploadEditorFileSuccess } from '@/store/templateEditor/actions';
import { AccessLevel, FormSettingsEnum } from '@dev/ee-api/ee-api-models';
import { ElasticTheme, palette } from '@dev/ee-components';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import {
    TemplateEditor as Editor,
    EditorCreateSignUpFieldEventCallback,
    EditorSaveData,
    EditorSaveError,
    EditorSignUpField,
    EditorTemplateStatus,
    EditorTemplateVersion,
    EditorWebsiteModeOutputFormat,
    FileType
} from '@dev/template-editor';
import { Portal } from '@material-ui/core';
import { includes, remove } from 'lodash-es';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useIntercom } from 'react-use-intercom';
import styled from 'styled-components';
import { useTemplateEditorFormMergeFields } from '@/services/hooks/useTemplateEditorFormMergeFields';
import { useBrandColorsInTemplateEditor } from '@/services/hooks/useBrandColorsInTemplateEditor';
import { FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT } from '../../constants';
import { useContactLists } from '../../hooks/useContactLists';
import { useCreateFieldModalParams } from '../../hooks/useCreateFieldModalParams';
import { useCreateListModalParams } from '../../hooks/useCreateListModalParams';
import { websiteStyles } from '../../tools/getWebsiteStyles';
import { RecaptchaLockedInfo } from '../RecaptchaLockedInfo';
import { UpsellingOverlay } from '../UpsellingOverlay';
import { TEMPLATE_EDITOR_NO_BLOCK_LIMIT } from '@/components/CheckoutCreator/constants';

const Overlay = styled.div`
    position: fixed;
    inset: 0;
    z-index: ${ElasticTheme.zIndex.modal};
    background: ${palette.snow[1]};

    .MuiContainer-root {
        padding-top: 60px;
    }
`;

const headerOptions = [];

interface TemplateEditorModalProps {
    isPopupCreator: boolean;
    isDismissButtonVisible: boolean;
}

// eslint-disable-next-line max-lines-per-function
export const TemplateEditorModal = ({ isPopupCreator, isDismissButtonVisible }: TemplateEditorModalProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation();
    const { PublicAccountID } = useSelector(getGeneralInfo);
    const dialog = useSelector(getDialogStore);
    const { template, contactLists, formObject, formPayload } = useSelector(getFormCreatorStore);
    const mediaManager = useSelector(getMediaManagerStore);
    const files = useSelector(getEditorFilesStore);
    const templateRef = useRef(template);
    const { showTemplateIsBeingSavedToast } = useShowTemplateIsBeingSavedToast();
    const { boot: bootIntercom, shutdown: shutdownIntercom } = useIntercom() || {};
    const [showImageTabOnly, setShowImageTabOnly] = useState(false);
    const [isLinkToFile, setIsLinkToFile] = useState(false);
    const [currentBlockId, setCurrentBlockId] = useState<string>();
    const [templateContent, setTemplateContent] = useState<EditorTemplateVersion>(null);
    const { fields } = useTemplateEditorFormMergeFields();
    const lists = useContactLists();
    const {
        AdvancedOptions: { EnableContactFeatures: hasContactFeatures },
    } = useSelector(getAccount);
    const { brandColors, onBrandColorAdd, onBrandColorEdit, onBrandColorDelete } = useBrandColorsInTemplateEditor();
    const { isUpsellingVisible } = useUpselling(AccessLevel.ViewCustomFields);
    const historyBackAction = useHistoryBackActionContext();
    const { pushBackCallback, popBackCallback } = historyBackAction || {};
    const { getCreateFieldModalProps } = useCreateFieldModalParams({ popBackCallback });
    const { getCreateListModalProps } = useCreateListModalParams({ popBackCallback });

    const isPlanHaveRecaptchaFeature = usePlanHaveRecaptchaFeature();

    useEffect(() => {
        templateRef.current = template;
    }, [template]);
    useEffect(() => {
        dispatch(ListsGetNamesStart());
        shutdownIntercom();
        pushBackCallback(onBreadcrumbAction);
        // dispatch(SetToastPortalWrapper(true));
        document.documentElement.style.overflow = 'auto';
        return () => {
            // dispatch(SetToastPortalWrapper(false));
            isIntercomEnabled() && bootIntercom();
            bootIntercom();
            document.documentElement.style.overflow = null;
        };
    }, []);
    useEffect(() => {
        if (
            templateContent ||
            !template?.loadedJson ||
            ![EditorTemplateStatus.LOADED, EditorTemplateStatus.SAVED].includes(templateRef.current.status)
        ) {
            return;
        }
        setTemplateContent(template?.loadedJson);
    }, [template?.loadedJson]);

    const onContactsListsChange = (contactLists: string[]): void => {
        dispatch(UpdateContactLists(contactLists));
    };
    const onTemplateChanged = () => {
        dispatch(TemplateChanged());
    };
    const onSave = (data: EditorSaveData, exitOnSave: boolean, errors?: EditorSaveError[]) => {
        dispatch<any>(TemplateSaveStart({ data, exitOnSave, errors })).then(() => {
            if (!exitOnSave || errors) {
                return;
            }
            popBackCallback();
        });
    };
    const onBreadcrumbAction = (): boolean => {
        if (
            templateRef.current.status === EditorTemplateStatus.SAVING ||
            templateRef.current.status === EditorTemplateStatus.MODIFIED
        ) {
            showTemplateIsBeingSavedToast();
            return false;
        }

        if (
            (!templateRef.current.isNew && templateRef.current.saveErrors) ||
            templateRef.current.status === EditorTemplateStatus.SAVE_ERROR
        ) {
            dispatch(TemplateShowSaveErrors());
            return false;
        }
        dispatch(SetIsTemplateOpen(false));
        return true;
    };
    const onBreadcrumbClick = (): void => {
        onBreadcrumbAction() && popBackCallback();
    };
    const onPreviewTemplateOpen = (templateHTML: string) => {
        dispatch(SetTemplatePreview(templateHTML));
    };
    const closeModal = (): boolean => {
        dispatch(HideDialog());
        return true;
    };
    const onCreateSignUpField = (callback: EditorCreateSignUpFieldEventCallback) => {
        if (isUpsellingVisible) {
            dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.CustomFields));
            return;
        }
        pushBackCallback(closeModal);
        dispatch(ShowDialog(getCreateFieldModalProps(callback)));
    };
    const onUpdateSingUpFields = (fields: EditorSignUpField[]): void => {
        dispatch(
            ModifyForm({
                MergeFields: fields?.map((item) => item.name)?.join(','),
            })
        );
    };

    const onCreateNewList = () => {
        dispatch(ShowDialog(getCreateListModalProps()));
        pushBackCallback(closeModal);
    };

    const onMailingPreferencesChange = (enableMailingPreferences: boolean): void => {
        const formSettings = formPayload?.FormSettings ?? formObject?.FormSettings ?? [];
        const isEnableMailingPreferenceInSettings = includes(formSettings, FormSettingsEnum.MailingPreference);
        const isFormSettingsUpToDate = enableMailingPreferences === isEnableMailingPreferenceInSettings;

        if (isFormSettingsUpToDate) {
            return;
        }

        const newFormSettings = enableMailingPreferences
            ? [...formSettings, FormSettingsEnum.MailingPreference]
            : remove([...formSettings], (setting) => setting !== FormSettingsEnum.MailingPreference);

        dispatch(
            ModifyForm({
                FormSettings: newFormSettings,
            })
        );
    };

    const openMediaManager = (blockId: string, isLinkToFile: boolean): void => {
        setCurrentBlockId(blockId);
        setShowImageTabOnly(!isLinkToFile);
        setIsLinkToFile(isLinkToFile);
        dispatch(ShowMediaManager());
    };

    const uploadFile = (file: File, blockId: string): void => {
        dispatch(UploadEditorFile(file, blockId));
    };

    const editorFilesUpdate = (files: FileType[]): void => {
        dispatch(UploadEditorFileSuccess(files));
    };

    const onRecaptchaUpselling = (): void => {
        dispatch(OpenUpsellingOverlay(AvailableFeaturesUpselling.Recaptcha));
    };

    return (
        <Portal>
            <Overlay>
                <Editor
                    translations={
                        i18n.hasResourceBundle(i18n.language, 'template-editor')
                            ? i18n.getResourceBundle(i18n.language, 'template-editor')
                            : null
                    }
                    template={{
                        status: template.status,
                        name: formObject?.Name,
                        isNameChangeDisabled: true,
                        content: templateContent,
                    }}
                    websiteModeOptions={{
                        output: {
                            format: EditorWebsiteModeOutputFormat.FullTemplate,
                        },
                        styles: websiteStyles,
                        rows: {
                            hide: false,
                        },
                        content: {
                            hide: false
                        },
                        isDismissButtonVisible: isDismissButtonVisible ||isPopupCreator,
                        blocks: {
                            paragraph: {
                                minOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            heading: {
                                minOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            image: {
                                minOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT, //no limits
                                maxOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT, //no limits
                            },
                            form: {
                                minOccurrences: 1,
                                maxOccurrences: 1,
                                minOccurrencesError: {
                                    name: 'FORM_BLOCK_MIN',
                                    message: t('youmustaddformblock'),
                                },
                                selectedListsError: {
                                    name: 'FORM_NO_SELECTED_LISTS',
                                    message: t('fixerrorstocontinue'),
                                },
                                maxOccurrencesReachedTooltip: t('formblocktooltip'),
                            },
                            video: {
                                minOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: FORM_TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            spacer: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            list: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            divider: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                            social: {
                                minOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                                maxOccurrences: TEMPLATE_EDITOR_NO_BLOCK_LIMIT,
                            },
                        },
                        signUpSettings: {
                            actionUrl: `${APIPATH}/v4/contact/form`,
                            target: '_self',
                            hiddenInputs: {
                                publicaccountid: PublicAccountID,
                                publicformid: formObject.PublicFormID,
                            },
                            fields,
                            lists,
                            selectedLists: contactLists,
                        },
                        components: {
                            recaptchaLockedInfoComponent: isPlanHaveRecaptchaFeature?.ViewAntiSpamUpsellingBanner ? (
                                <RecaptchaLockedInfo
                                    button={{
                                        text: t('upgradetounlock'),
                                        startIcon: <LockIcon width={20} height={20} />,
                                        onClick: onRecaptchaUpselling,
                                    }}
                                />
                            ) : null,
                        },
                        isRecaptchaHidden: !isPlanHaveRecaptchaFeature?.ViewSubscriptionAntiSpamFeature,
                    }}
                    headerOptions={headerOptions}
                    events={{
                        onFileUpload: uploadFile,
                        onMediaManagerOpen: openMediaManager,
                        onEditorFilesUpdate: editorFilesUpdate,
                        onTemplateChanged,
                        onSave,
                        onTemplatePreview: onPreviewTemplateOpen,
                        onBreadcrumbClick,
                        onBrandColorAdd,
                        onBrandColorDelete,
                        onBrandColorEdit,
                        onCreateSignUpField: hasContactFeatures ? onCreateSignUpField : null,
                        onContactsListsChange,
                        onUpdateSingUpFields,
                        onCreateNewList,
                        onMailingPreferencesChange,
                    }}
                    media={{
                        mediaManagerComponent: {
                            content: (
                                <EditorMediaManager
                                    showImageTabOnly={showImageTabOnly}
                                    isLinkToFile={isLinkToFile}
                                    blockId={currentBlockId}
                                    isOpen={mediaManager.isOpened}
                                />
                            ),
                            isMediaManagerOpen: mediaManager.isOpened,
                        },
                        files,
                    }}
                    displayOptions={{
                        isBelowOtherLayer: dialog.isOpened,
                        popupsMinimalZIndex: 1400,
                    }}
                    autosaveOptions={{
                        counter: template.autosaveCounter,
                    }}
                    navigation={{
                        breadcrumbText: t('backtoform'),
                        isContinueButtonVariant: true,
                    }}
                    linkToOptions={{ mergeFields: linkToMergeFields }}
                    colorPickerOptions={{
                        recentColors: template.recentColors,
                        brandColors,
                    }}
                />
            </Overlay>
            <UpsellingOverlay />
        </Portal>
    );
};
